import { LocalDate, ZonedDateTime } from '@js-joda/core';
import findLast from 'lodash/findLast';
import orderBy from 'lodash/orderBy';
import {
  ChatRoomUsersConnFragment,
  MessageDetailsFragment,
} from '../../graphql/types';
import {
  getMostRecentActivityDate,
  getTimeSince,
  stringToLocalDate,
  stringToZonedDateTime,
} from '../../utils/date';
import { getMemberName } from '../../utils/getMemberName';
import { PhotoUserFragment } from '../reusable/Avatar';
import { MemberChatBasicInfo } from './Context';

const getHeaderLastActiveLabel = (lastActivityDate: ZonedDateTime | null) =>
  lastActivityDate
    ? `Active ${getTimeSince(
        lastActivityDate.toLocalDate(),
        LocalDate.now(),
      ).toLocaleLowerCase()}`
    : null;

export interface HeaderContent {
  title: string;
  subtitle?: string | null;
  avatarMember?: PhotoUserFragment | null;
  hideEmptyElements?: boolean;
}

export const getMemberHeaderContent = ({
  member,
}: {
  member: MemberChatBasicInfo | null | undefined;
}): HeaderContent => {
  const lastActivityDate = getMostRecentActivityDate(member?.lastActivity);
  return {
    title: getMemberName(member),
    subtitle: getHeaderLastActiveLabel(lastActivityDate),
    avatarMember: member,
  };
};

export type MessageWithGroupingMetadata = {
  isFirstMessageInDay: boolean; // example use: displaying a date separator above the first message for a given day
  isFirstMessageInGroup: boolean; // example use: displaying a message's timestamp and sender name above the first message in a group
  isLastMessageInGroup: boolean; // example use: displaying a message's senders avatar beside the last message in a group
  isLastReadMessage: boolean; // example use: displaying a read receipt
} & MessageDetailsFragment;

export const getMessagesWithGroupingMetadata = ({
  messages,
  chatUsers = [],
  groupWithinMinutes = 5,
}: {
  messages: MessageDetailsFragment[];
  chatUsers?: ChatRoomUsersConnFragment['edges'];
  groupWithinMinutes?: number;
}) => {
  if (process.env.NODE_ENV === 'test') {
    /**
     * accidentally passing in unsorted messages cause tough to debug issues with the following logic
     * this helps guide a future test writer so they don't get lost in a rabbit hole
     */
    for (let i = 1; i < messages.length; i += 1) {
      if (
        stringToZonedDateTime(messages[i].createdAt).isBefore(
          stringToZonedDateTime(messages[i - 1].createdAt),
        )
      ) {
        // biome-ignore lint/suspicious/noConsole: eslint migration
        console.warn(
          `Received message at index ${i} is not sorted, this will cause issues with grouping`,
        );
      }
    }
  }

  let previousDay: LocalDate | null = null;
  let previousMessage: MessageDetailsFragment | null = null;

  const messagesWithMetadata: MessageWithGroupingMetadata[] = [];

  const uniqueSenders = new Set<string>();

  messages.forEach((message) => {
    // keep track of unique senders to calculate `isLastReadMessage`
    uniqueSenders.add(message.user.id);

    const currentMessageDay = stringToLocalDate(message.createdAt);
    let isFirstMessageInDay = false;
    let isFirstMessageInGroup = false;

    // Check if message is in the same day as the previous message
    if (!currentMessageDay.equals(previousDay)) {
      previousDay = currentMessageDay;
      isFirstMessageInDay = true;
      isFirstMessageInGroup = true;
    }

    // Check if message is from a different user than the previous message
    if (previousMessage?.user.id !== message.user.id) {
      isFirstMessageInGroup = true;
    }

    /**
     * Check if message is is within `groupWithinMinutes` minutes of previous message
     */
    const upperBound = previousMessage?.createdAt
      ? stringToZonedDateTime(previousMessage.createdAt).plusMinutes(
          groupWithinMinutes,
        )
      : null;

    if (
      upperBound &&
      stringToZonedDateTime(message.createdAt).isAfter(upperBound)
    ) {
      isFirstMessageInGroup = true;
    }

    // Finally, if none of the above conditions are met, then the message is not the first in a group or day
    previousMessage = message;
    const lastMessageWithMetadata = messagesWithMetadata.at(-1);
    if (!isFirstMessageInGroup && lastMessageWithMetadata) {
      lastMessageWithMetadata.isLastMessageInGroup = false;
    }
    messagesWithMetadata.push({
      ...message,
      isFirstMessageInDay,
      isFirstMessageInGroup,
      isLastMessageInGroup: true,
      isLastReadMessage: false,
    });
  });

  uniqueSenders.forEach((senderId) => {
    const otherChatUsers = chatUsers.filter(
      (user) => user.node.id !== senderId,
    );

    // Get the earliest of all other user's last read times
    const lastReadByAllOtherUsersAt = orderBy(otherChatUsers, [
      'lastMessageRead',
    ])[0]?.lastMessageRead;

    // get first message from this sender who's created at time is before the minimum last read time
    if (lastReadByAllOtherUsersAt) {
      const lastReadMessage = findLast(
        messagesWithMetadata,
        ({ createdAt, user }) =>
          user.id === senderId &&
          (stringToZonedDateTime(lastReadByAllOtherUsersAt).isAfter(
            ZonedDateTime.parse(createdAt),
          ) ||
            stringToZonedDateTime(lastReadByAllOtherUsersAt).isEqual(
              ZonedDateTime.parse(createdAt),
            )),
      );

      // mark it as the last read message
      if (lastReadMessage) {
        lastReadMessage.isLastReadMessage = true;
      }
    }
  });

  return messagesWithMetadata;
};

export const getScrollableDivInChat = (
  scrollRef: React.RefObject<HTMLDivElement>,
) => {
  // InfiniteScroll doesn't pass the ref to the node where the scrolling occurs
  const scrollableDiv = scrollRef?.current?.childNodes[0]
    ?.childNodes[0] as HTMLDivElement;

  return scrollableDiv;
};
